import React from 'react';
import { Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconClock, IconMapPin } from '@tabler/icons-react';
import { IServiceRequestSummary } from '../interfaces/heavyDutyMachinery/IServiceRequest';
import {
  SERVICE_REQUEST_STATUS_ICON,
  SERVICE_REQUEST_STATUS_LABEL_KEYS,
  SERVICE_REQUEST_STATUS_TONES,
  SERVICE_REQUEST_URGENCY_LABEL_KEYS,
  SERVICE_REQUEST_URGENCY_TONES,
} from '../constants/serviceRequestOptions';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatCurrency } from '../helpers/currencyHelper';
import { formatShortDateTime } from '../helpers/dateFormatHelper';
import { pillToneStyle, TEAL } from '../theme/creamyTokens';

const { Text } = Typography;

interface ServiceRequestCardProps {
  request: IServiceRequestSummary;
  onOpen: () => void;
}

const pill = (style: React.CSSProperties): React.CSSProperties => ({
  ...style,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  borderRadius: 999,
  fontSize: 11,
  whiteSpace: 'nowrap',
});

/**
 * One request in a list, on either side.
 *
 * The same card for the customer and the professional on purpose: it is the
 * same row of facts, and the only thing that differs is whose name is on it,
 * which the server already resolved into `peerDisplayName`.
 *
 * What leads is the DESCRIPTION, not the title of a listing. A professional
 * triaging ten of these decides from "se me moja la pared del baño", never
 * from "Fontanería general".
 */
const ServiceRequestCard: React.FC<ServiceRequestCardProps> = ({ request, onOpen }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const StatusIcon = SERVICE_REQUEST_STATUS_ICON[request.status];
  // Two clocks, never both at once, and both of them the professional's:
  // answering the request (Sent) and then fixing the visit (Accepted). One
  // pill either way -- what changes is the sentence.
  const secondsLeft = request.respondInSeconds ?? request.confirmInSeconds;
  const hoursLeft = secondsLeft != null ? secondsLeft / 3600 : null;
  const clockLabelKey = request.respondInSeconds != null
    ? TRANSLATIONS.SERVICE_REQUEST_HOURS_LEFT
    : TRANSLATIONS.SERVICE_REQUEST_CONFIRM_HOURS_LEFT;

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
        borderRadius: 16,
        padding: 14,
        marginBottom: 10,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 8 }}>
        <span style={pill(pillToneStyle(SERVICE_REQUEST_STATUS_TONES[request.status], token))}>
          <StatusIcon size={11} />
          {translate(t, SERVICE_REQUEST_STATUS_LABEL_KEYS[request.status])}
        </span>
        <span style={pill(pillToneStyle(SERVICE_REQUEST_URGENCY_TONES[request.urgency], token))}>
          {translate(t, SERVICE_REQUEST_URGENCY_LABEL_KEYS[request.urgency])}
        </span>
        {request.currentAmount != null && (
          <Text strong style={{ marginLeft: 'auto', fontSize: 15, color: TEAL, whiteSpace: 'nowrap' }}>
            {formatCurrency(request.currentAmount, request.currencyCode, i18n.language)}
          </Text>
        )}
      </div>

      <Text style={{ fontSize: 14, display: 'block', marginBottom: 6 }}>{request.descriptionPreview}</Text>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {request.peerDisplayName}
        </Text>
        {request.city && (
          <Text type="secondary" style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <IconMapPin size={12} />
            {request.city}
          </Text>
        )}
        <Text type="secondary" style={{ fontSize: 12, marginLeft: 'auto' }}>
          {formatShortDateTime(request.createdAt, i18n.language)}
        </Text>
      </div>

      {/* Only while a clock is actually running, and it never runs on the
          customer: nobody is timed deciding how much to spend, and
          withdrawing is always theirs to do. */}
      {hoursLeft != null && hoursLeft > 0 && (
        <Text
          style={{
            fontSize: 11,
            marginTop: 8,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: hoursLeft < 4 ? token.colorError : token.colorTextTertiary,
          }}
        >
          <IconClock size={11} />
          {translate(t, clockLabelKey, { hours: Math.max(1, Math.round(hoursLeft)) })}
        </Text>
      )}
    </button>
  );
};

export default ServiceRequestCard;
